/**
 * useContent — TanStack Query hooks for daily pregnancy content.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import {
  getDayContent,
  saveDayContent,
  deleteDayContent,
  getAudienceDayStatuses,
} from '@/services/contentService';
import type { Audience } from '@/types/auth.types';
import type { DailyContentInput } from '@/types/content.types';

// ─── Query keys ────────────────────────────────────────────────────────────────

export const contentKeys = {
  all:             ['content'] as const,
  audience:        (a: Audience) => ['content', a] as const,
  audienceStatuses:(a: Audience) => ['content', a, 'statuses'] as const,
  day:             (a: Audience, d: number) => ['content', a, 'day', d] as const,
};

// ─── Audience status map ───────────────────────────────────────────────────────

/**
 * Load ALL day statuses for an audience.
 * Cached and shared between month cards — only one Firestore query per audience.
 */
export function useAudienceStatuses(audience: Audience) {
  return useQuery({
    queryKey: contentKeys.audienceStatuses(audience),
    queryFn:  () => getAudienceDayStatuses(audience),
    staleTime: 2 * 60 * 1000, // 2 min
  });
}

// ─── Single day content ────────────────────────────────────────────────────────

/** Load a single day's content for the editor. */
export function useDayContent(audience: Audience, dayNumber: number) {
  return useQuery({
    queryKey: contentKeys.day(audience, dayNumber),
    queryFn:  () => getDayContent(audience, dayNumber),
    staleTime: 30 * 1000,
  });
}

// ─── Save / delete mutations ───────────────────────────────────────────────────

export function useSaveDayContent() {
  const queryClient = useQueryClient();
  const { cmsUser }  = useAuth();

  return useMutation({
    mutationFn: async ({
      audience,
      dayNumber,
      input,
      isNew,
    }: {
      audience:  Audience;
      dayNumber: number;
      input:     DailyContentInput;
      isNew:     boolean;
    }) => {
      if (!cmsUser) throw new Error('Not authenticated');
      const userRef = { uid: cmsUser.uid, displayName: cmsUser.displayName, email: cmsUser.email };
      await saveDayContent(audience, dayNumber, input, userRef, isNew);
    },
    onSuccess: (_, { audience, dayNumber }) => {
      // Invalidate single day + the audience status map
      queryClient.invalidateQueries({ queryKey: contentKeys.day(audience, dayNumber) });
      queryClient.invalidateQueries({ queryKey: contentKeys.audienceStatuses(audience) });
    },
  });
}

export function useDeleteDayContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ audience, dayNumber }: { audience: Audience; dayNumber: number }) =>
      deleteDayContent(audience, dayNumber),
    onSuccess: (_, { audience, dayNumber }) => {
      queryClient.removeQueries({ queryKey: contentKeys.day(audience, dayNumber) });
      queryClient.invalidateQueries({ queryKey: contentKeys.audienceStatuses(audience) });
    },
  });
}
